import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Monitor,
  Plus,
  Copy,
  Check,
  RefreshCw,
  ChevronRight,
  Clock,
  Wifi,
  Activity,
  Globe,
  KeyRound,
} from 'lucide-react';
import { api, authedRequest } from '../../services/apiService';
import { hardwareApi, formFactorLabel } from '../../services/hardwareApi';
import { provisionTypeFromFormFactor, PROVISIONABLE_TERMINAL_TYPES } from './provisionType';
import SearchableDropdown from '../../components/SearchableDropdown';
import { toastService } from '../../services/toastService';
import { terminalDisplayStatus, LIGHT_BADGE } from '../../components/pos/terminalDisplayStatus';
import { RubyLoader } from '../../components/RubyLoader';
import ConfirmModal from '../../components/credits/ConfirmModal';
import { Segmented } from '../../components/pos/cardSetup/ReaderPicker';
import { MagtekReadinessChips } from '../../components/pos/unigate/MagtekReadinessChips';
import { type MagtekReadiness, resolveMagtekReadiness, magtekReady, PAYMENT_PROCESSORS_PATH } from '../../components/pos/unigate/unigateCreds';
import TerminalNewCodeModal, { NewTerminalCode } from '../../components/pos/TerminalNewCodeModal';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Terminal {
  terminalId: string;
  provisionCode: string;
  locationId: string;
  locationName: string;
  registerName: string;
  terminalType: 'pos' | 'kiosk' | 'customer_display' | 'android_dual_screen' | 'online_sales';
  status: 'pending' | 'activated';
  createdAt: number;
  expiresAt?: number;
  activatedAt?: number;
  lastSeenAt?: number;
  /** The REGISTER row's status; null when the terminal was never claimed. */
  registerStatus?: string | null;
  /** ISO expiry of the current activation code (codes are single use, 14 days). */
  codeExpiresAt?: string | null;
  codeExpired?: boolean;
  /** true when provisionCode is the masked display form (RT-****-AB12). */
  provisionCodeMasked?: boolean;
}

const formatCodeExpiry = (iso?: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

interface DejavooDevice {
  deviceId: string;
  deviceName: string;
  tpnMasked?: string;
  deviceClass?: string;
  originLocationId: string;
  originLocationName: string;
}

interface Props {
  tenantId: string;
  tenantName: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDateTime = (ts?: number | null): string => {
  if (!ts) return 'N/A';
  const normalized = ts < 946684800000 ? ts * 1000 : ts;
  return new Date(normalized).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * ONE source for terminal-type wording — `FORM_FACTOR_LABEL` in shared-types.
 *
 * This was a local `switch`, and there were five of them. They had already
 * drifted: the two in platform-app did not handle `android_dual_screen` at all,
 * so a K10 showed a tenant the raw string `android_dual_screen`.
 */
const typeLabel = (t: string) => formFactorLabel(t);

// Friendly text for online-terminal create error codes returned by the API.
const ONLINE_TERMINAL_ERROR_MESSAGES: Record<string, string> = {
  device_not_found: 'Selected device was not found. Please refresh and try again.',
  not_a_dejavoo_device: 'Selected device is not a Dejavoo processor.',
  missing_fields: 'Location and Dejavoo processor are required.',
  no_magtek_config: 'MagTek UniGate credentials are missing for this location. Set them under Payment processors.',
  no_teconnect_config: 'The MagTek website key is missing for this location. Set it under Payment processors.',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface TenantLocation {
  locationId: string;
  name: string;
  status: string;
}

// WebSocket status API
const getWsStatusUrl = () => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const base = hostname.includes('dev.') || hostname === 'localhost' ? 'dev.rubytill.com' : 'rubytill.com';
  return `https://pos-ws.api.${base}`;
};

const PosTerminalTab: React.FC<Props> = ({ tenantId, tenantName }) => {
  const navigate = useNavigate();
  const [terminals, setTerminals] = useState<Terminal[]>([]);
  const [loading, setLoading] = useState(true);
  const [onlineTerminals, setOnlineTerminals] = useState<Set<string>>(new Set());
  const [onlineSummary, setOnlineSummary] = useState({ online: 0, total: 0 });
  const [showForm, setShowForm] = useState(false);

  // Locations for dropdown
  const [locations, setLocations] = useState<TenantLocation[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);

  // Form state
  const [formLocationId, setFormLocationId] = useState('');
  const [formRegisterName, setFormRegisterName] = useState('');
  const [formType, setFormType] = useState<'pos' | 'kiosk' | 'customer_display' | 'android_dual_screen'>('pos');
  /** Catalog POS devices. Choosing one DERIVES the terminal type client-side —
   *  the provisioning endpoint still receives terminalType exactly as before. */
  const [deviceModels, setDeviceModels] = useState<any[]>([]);
  const [formDeviceModelId, setFormDeviceModelId] = useState<string>('');
  const [provisioning, setProvisioning] = useState(false);
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState(false);
  // "Generate new code" for an existing till
  const [newCodeFor, setNewCodeFor] = useState<Terminal | null>(null);
  const [newCodeBusy, setNewCodeBusy] = useState(false);
  const [newCodeResult, setNewCodeResult] = useState<NewTerminalCode | null>(null);

  // Online terminal ("Add online terminal") form state — separate action, no
  // provisioning code. Created directly via api.posNmi.createOnlineTerminal.
  const [showOnlineForm, setShowOnlineForm] = useState(false);
  const [onlineLocationId, setOnlineLocationId] = useState('');
  const [onlineName, setOnlineName] = useState('');
  const [dejavooDevices, setDejavooDevices] = useState<DejavooDevice[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [onlineDeviceId, setOnlineDeviceId] = useState('');
  // Combined flow: reference an existing processor OR create a new one inline.
  const [onlineMode, setOnlineMode] = useState<'existing' | 'new'>('existing');
  const [newProcName, setNewProcName] = useState('');
  const [newProcTpn, setNewProcTpn] = useState('');
  const [newProcAuthToken, setNewProcAuthToken] = useState('');
  // Processor brand for the new online terminal. MagTek = one click (credentials resolve at charge time).
  const [onlineProcessor, setOnlineProcessor] = useState<'dejavoo' | 'magtek'>('dejavoo');
  /** undefined = checking, null = could not check. */
  const [magtekReadiness, setMagtekReadiness] = useState<MagtekReadiness | null | undefined>(undefined);
  const [onlineSubmitting, setOnlineSubmitting] = useState(false);
  const [onlineError, setOnlineError] = useState<string | null>(null);

  // Get location name from selected locationId
  const selectedLocation = locations.find(l => l.locationId === formLocationId);
  const onlineSelectedLocation = locations.find(l => l.locationId === onlineLocationId);

  const fetchWsStatus = useCallback(async () => {
    try {
      const data = await authedRequest<{ terminals?: Array<{ terminalId: string }> }>(
        `${getWsStatusUrl()}/terminals/status?tenantId=${tenantId}`,
        { headers: { 'x-tenant-id': tenantId }, silent: true },
      );
      const ids = new Set<string>((data.terminals || []).map((t: any) => t.terminalId));
      setOnlineTerminals(ids);
      setOnlineSummary({ online: ids.size, total: 0 }); // total updated after terminals load
    } catch { /* non-critical */ }
  }, [tenantId]);

  const fetchTerminals = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.posTerminals.list(tenantId);
      const list = resp?.terminals || [];
      setTerminals(list);
      setOnlineSummary(prev => ({ ...prev, total: list.filter((t: Terminal) => t.status === 'activated' || !!t.registerStatus).length }));
    } catch (err: any) {
      toastService.error('Failed to load terminals: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const fetchLocations = useCallback(async () => {
    setLocationsLoading(true);
    try {
      const data: any = await api.locationBilling.listTenantRequests(tenantId);
      const activeLocations = (data?.requests || [])
        .filter((r: any) => r.status === 'active' && r.locationId)
        .map((r: any) => ({ locationId: r.locationId, name: r.name || r.locationId, status: r.status }));
      setLocations(activeLocations);
    } catch (err: any) {
      console.error('Failed to load locations:', err);
    } finally {
      setLocationsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchTerminals();
    fetchLocations();
    fetchWsStatus();
  }, [fetchTerminals, fetchLocations, fetchWsStatus]);

  useEffect(() => {
    // Optional enrichment: if the catalog is unreachable the picker simply stays
    // empty and provisioning works exactly as it did before.
    hardwareApi.listModels({ category: 'pos_terminal', status: 'active' })
      .then((r) => setDeviceModels(r.items || []))
      .catch(() => setDeviceModels([]));
  }, []);

  const handleProvision = async () => {
    if (!formLocationId || !formRegisterName.trim()) {
      toastService.warning('Location and Register Name are required');
      return;
    }
    setProvisioning(true);
    try {
      const locationName = selectedLocation?.name || formLocationId;
      const resp = await api.posTerminals.provision({
        tenantId,
        locationId: formLocationId,
        locationName,
        registerName: formRegisterName.trim(),
        terminalType: formType,
        ...(formDeviceModelId ? { deviceModelId: formDeviceModelId } : {}),
      });
      setGeneratedCode(resp.provisionCode);
      toastService.success('Terminal provisioned successfully');
      fetchTerminals();
    } catch (err: any) {
      toastService.error('Failed to provision: ' + (err.message || 'Unknown error'));
    } finally {
      setProvisioning(false);
    }
  };

  const handleGenerateNewCode = async () => {
    if (!newCodeFor) return;
    setNewCodeBusy(true);
    try {
      const resp = await api.posTerminals.newCode(newCodeFor.terminalId, tenantId);
      if (!resp?.code) throw new Error((resp as any)?.error || 'No code returned');
      setNewCodeResult({
        terminalId: resp.terminalId,
        registerName: resp.registerName || newCodeFor.registerName,
        code: resp.code,
        expiresAt: resp.expiresAt,
        reopened: resp.reopened,
        message: resp.message,
      });
      setNewCodeFor(null);
      fetchTerminals();
    } catch (err: any) {
      toastService.error('Could not generate a new code: ' + (err?.message || 'Unknown error'));
    } finally {
      setNewCodeBusy(false);
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const resetForm = () => {
    setShowForm(false);
    setFormLocationId('');
    setFormRegisterName('');
    setFormType('pos');
    setFormDeviceModelId('');
    setGeneratedCode(null);
    setCopiedCode(false);
  };

  // Fetches every Dejavoo processor registered anywhere for this tenant —
  // one TPN can be linked to online terminals at multiple locations, so the
  // processor picker is independent of which location the online terminal
  // is being created at. Queries all locations in parallel and tolerates
  // per-location failures (a location with no devices/access is just skipped).
  const fetchAllDejavooDevices = useCallback(async () => {
    setDevicesLoading(true);
    try {
      // Online terminals use Dejavoo CLOUD processors — one tenant-wide call
      // returns them across all locations (home location labels the picker).
      const resp = await api.posNmi.listOnlineProcessors(tenantId);
      const locName = (id: string) => locations.find((l) => l.locationId === id)?.name || id;
      setDejavooDevices((resp.processors || []).filter((p) => p.kind !== 'magtek').map((p): DejavooDevice => ({
        deviceId: p.deviceId,
        deviceName: p.deviceName,
        tpnMasked: p.tpnMasked || undefined,
        deviceClass: 'dejavoo_cloud',
        originLocationId: p.locationId,
        originLocationName: locName(p.locationId),
      })));
    } catch {
      setDejavooDevices([]);
    } finally {
      setDevicesLoading(false);
    }
  }, [tenantId, locations]);

  const resetOnlineForm = () => {
    setShowOnlineForm(false);
    setOnlineLocationId('');
    setOnlineName('');
    setDejavooDevices([]);
    setOnlineDeviceId('');
    setOnlineMode('existing');
    setOnlineProcessor('dejavoo');
    setMagtekReadiness(undefined);
    setNewProcName('');
    setNewProcTpn('');
    setNewProcAuthToken('');
    setOnlineError(null);
  };

  const handleToggleOnlineForm = () => {
    if (showOnlineForm) {
      resetOnlineForm();
    } else {
      setShowForm(false);
      setShowOnlineForm(true);
    }
  };

  // Fetch (or re-fetch) the tenant's Dejavoo processors once the panel is
  // open and locations have finished loading — locations may still be
  // loading at the moment the panel is toggled open.
  useEffect(() => {
    if (showOnlineForm && !locationsLoading) {
      fetchAllDejavooDevices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOnlineForm, locationsLoading]);

  // MagTek: mirror the backend rule from the platform + location credential status.
  useEffect(() => {
    if (!showOnlineForm || onlineProcessor !== 'magtek' || !onlineLocationId) { setMagtekReadiness(undefined); return; }
    let live = true;
    setMagtekReadiness(undefined);
    Promise.all([api.posNmi.getMagtekUnigatePlatform(), api.posNmi.getLocationMagtekCreds(tenantId, onlineLocationId)])
      .then(([platform, loc]) => { if (live) setMagtekReadiness(resolveMagtekReadiness(loc, platform)); })
      .catch(() => { if (live) setMagtekReadiness(null); });
    return () => { live = false; };
  }, [showOnlineForm, onlineProcessor, onlineLocationId, tenantId]);

  const handleToggleProvisionForm = () => {
    if (showForm) {
      resetForm();
    } else {
      resetOnlineForm();
      setShowForm(true);
    }
  };

  const handleCreateOnlineTerminal = async () => {
    const usingNew = onlineMode === 'new';
    const usingMagtek = onlineProcessor === 'magtek';
    if (usingMagtek) {
      if (!onlineLocationId) { setOnlineError('Location is required'); return; }
      setOnlineError(null);
      setOnlineSubmitting(true);
      try {
        await api.posNmi.createOnlineTerminal(tenantId, {
          locationId: onlineLocationId,
          locationName: onlineSelectedLocation?.name || onlineLocationId,
          name: onlineName.trim() || 'Online Store',
          newProcessor: { kind: 'magtek' },
        });
        toastService.success('Online terminal created');
        resetOnlineForm();
        fetchTerminals();
      } catch (err: any) {
        const code = err?.code || err?.message || '';
        setOnlineError(ONLINE_TERMINAL_ERROR_MESSAGES[code] || err?.serverMessage || code || 'Failed to create online terminal');
      } finally {
        setOnlineSubmitting(false);
      }
      return;
    }
    if (!onlineLocationId || (usingNew ? (!newProcName.trim() || !newProcTpn.trim() || !newProcAuthToken.trim()) : !onlineDeviceId)) {
      setOnlineError(usingNew ? 'Location, processor nickname, TPN and Auth Token are required' : 'Location and Dejavoo processor are required');
      return;
    }
    setOnlineError(null);
    setOnlineSubmitting(true);
    try {
      const locationName = onlineSelectedLocation?.name || onlineLocationId;
      await api.posNmi.createOnlineTerminal(tenantId, {
        locationId: onlineLocationId,
        locationName,
        name: onlineName.trim() || 'Online Store',
        ...(usingNew
          ? { newProcessor: { deviceName: newProcName.trim(), spinTpn: newProcTpn.trim(), iposAuthToken: newProcAuthToken.trim() } }
          : { emvDeviceId: onlineDeviceId }),
      });
      toastService.success('Online terminal created');
      resetOnlineForm();
      fetchTerminals();
    } catch (err: any) {
      const code = err?.message || '';
      setOnlineError(ONLINE_TERMINAL_ERROR_MESSAGES[code] || code || 'Failed to create online terminal');
    } finally {
      setOnlineSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-stone-800 font-semibold text-sm">POS Terminals</h3>
            <p className="text-stone-500 text-xs mt-1">
              Provision and manage POS terminals for {tenantName}
              {onlineSummary.total > 0 && (
                <span className="ml-2 text-emerald-600">
                  <Activity size={10} className="inline mr-1" />
                  {onlineSummary.online}/{onlineSummary.total} online
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              onClick={fetchTerminals}
              className="p-2 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-500 hover:text-stone-700 transition-colors"
              title="Refresh"
            >
              <RefreshCw size={14} />
            </button>
            <button
              onClick={handleToggleProvisionForm}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 transition-colors min-h-[36px]"
            >
              <Plus size={14} /> Create Terminal
            </button>
            <button
              onClick={handleToggleOnlineForm}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 transition-colors min-h-[36px]"
            >
              <Globe size={14} /> Add Online Terminal
            </button>
          </div>
        </div>
      </div>

      {/* Create Terminal Form */}
      {showForm && (
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5 space-y-4">
          <h3 className="text-stone-800 font-semibold text-sm">Create New POS Terminal</h3>

          {!generatedCode ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="text-stone-500 text-xs mb-1 block">Location *</label>
                  {locationsLoading ? (
                    <div className="flex items-center gap-2 text-stone-400 text-sm py-2">
                      <RubyGemMark size={14} drawing /> Loading locations...
                    </div>
                  ) : locations.length === 0 ? (
                    <p className="text-amber-600 text-xs py-2">No active locations found for this tenant.</p>
                  ) : (
                    <SearchableDropdown
                      options={locations.map(loc => ({ value: loc.locationId, label: loc.name }))}
                      value={formLocationId || null}
                      onChange={(v) => setFormLocationId(v || '')}
                      placeholder="— Select Location —"
                      ariaLabel="Location"
                      searchPlaceholder="Search locations..."
                    />
                  )}
                </div>
                <div>
                  <label className="text-stone-500 text-xs mb-1 block">Register Name *</label>
                  <input
                    type="text"
                    value={formRegisterName}
                    onChange={(e) => setFormRegisterName(e.target.value)}
                    placeholder="Register 1"
                    className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-stone-800 text-sm"
                  />
                </div>
                <div>
                  <label className="text-stone-500 text-xs mb-1 block">Device Model</label>
                  <SearchableDropdown
                    options={deviceModels.map((m: any) => ({
                      value: m.modelId,
                      label: m.modelName,
                      subtitle: [m.manufacturer, m.specs?.androidVersion && `Android ${m.specs.androidVersion}`]
                        .filter(Boolean).join(' · '),
                    }))}
                    value={formDeviceModelId || null}
                    onChange={(v) => {
                      setFormDeviceModelId(v || '');
                      // Derive the terminal type from the chosen device. The wire
                      // format is unchanged — the server still receives terminalType.
                      const m = deviceModels.find((d: any) => d.modelId === v);
                      // Only a value provisioning actually honours is copied.
                      // `not_applicable` means the catalog says this item is not
                      // a terminal, and a free-form value left over from before
                      // the list was closed is rewritten to 'pos' by the handler
                      // SILENTLY — so neither is adopted here; the operator is
                      // left to choose instead of being shown a type that will
                      // not survive the round trip.
                      const derived = provisionTypeFromFormFactor(m?.appProfile?.terminalFormFactor);
                      if (derived) setFormType(derived as any);
                    }}
                    placeholder={deviceModels.length ? 'Select a device (optional)' : 'No devices in catalog'}
                    searchPlaceholder="Search devices..."
                    disabled={deviceModels.length === 0}
                  />
                  <p className="text-[11px] text-stone-500 mt-1">
                    {deviceModels.length === 0
                      ? <>Add POS devices in <a href="/pos-config" className="underline underline-offset-2">Device Management</a>.</>
                      : 'Sets the terminal type automatically.'}
                  </p>
                </div>
                <div>
                  <label className="text-stone-500 text-xs mb-1 block">Terminal Type</label>
                  <SearchableDropdown
                    // Labels come from the SHARED map, so this picker and the
                    // device editor's form-factor list cannot drift apart.
                    // The types a TERMINAL may be — `not_applicable` is a
                    // catalog answer ("never becomes a terminal"), so it is not
                    // offered here.
                    options={PROVISIONABLE_TERMINAL_TYPES
                      .map((v) => ({ value: v, label: formFactorLabel(v) }))}
                    value={formType}
                    onChange={(v) => setFormType((v as any) || 'pos')}
                    placeholder="Select terminal type"
                    searchPlaceholder="Search types..."
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={resetForm}
                  className="px-3 py-2 rounded-lg text-xs font-medium text-stone-600 bg-stone-100 hover:bg-stone-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleProvision}
                  disabled={provisioning}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 min-h-[36px]"
                >
                  {provisioning ? (
                    <><RubyGemMark size={14} drawing color="currentColor" /> Generating...</>
                  ) : (
                    'Generate Code'
                  )}
                </button>
              </div>
            </>
          ) : (
            /* Generated Code Display */
            <div className="text-center py-4">
              <p className="text-stone-500 text-xs mb-3">
                Provisioning code generated. Enter this on the POS terminal:
              </p>
              <div className="inline-flex items-center gap-3 bg-stone-50 border-2 border-emerald-500/30 rounded-xl px-6 py-4">
                <span className="text-3xl font-mono font-bold text-emerald-600 tracking-widest select-all">
                  {generatedCode}
                </span>
                <button
                  onClick={() => handleCopyCode(generatedCode)}
                  className="p-2 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-500 hover:text-stone-700 transition-colors"
                  title="Copy code"
                >
                  {copiedCode ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
                </button>
              </div>
              <p className="text-amber-600 text-xs mt-3 flex items-center justify-center gap-1.5">
                <Clock size={12} /> Expires in 14 days · works once
              </p>
              <button
                onClick={resetForm}
                className="mt-4 px-4 py-2 rounded-lg text-xs font-medium text-stone-600 bg-stone-100 hover:bg-stone-200"
              >
                Done
              </button>
            </div>
          )}
        </div>
      )}

      {/* Add Online Terminal Panel — separate action, no provisioning code */}
      {showOnlineForm && (
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5 space-y-4">
          <div>
            <h3 className="text-stone-800 font-semibold text-sm">Add Online Terminal</h3>
            <p className="text-stone-500 text-xs mt-1">
              Creates a virtual terminal for online/storefront sales, linked to any of this business's Dejavoo processors — even one registered at a different location. No provisioning code needed.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-stone-500 text-xs mb-1 block">Location *</label>
              {locationsLoading ? (
                <div className="flex items-center gap-2 text-stone-400 text-sm py-2">
                  <RubyGemMark size={14} drawing /> Loading locations...
                </div>
              ) : locations.length === 0 ? (
                <p className="text-amber-600 text-xs py-2">No active locations found for this tenant.</p>
              ) : (
                <SearchableDropdown
                  options={locations.map(loc => ({ value: loc.locationId, label: loc.name }))}
                  value={onlineLocationId || null}
                  onChange={(v) => setOnlineLocationId(v || '')}
                  placeholder="— Select Location —"
                  ariaLabel="Location"
                  searchPlaceholder="Search locations..."
                />
              )}
            </div>
            <div>
              <label className="text-stone-500 text-xs mb-1 block">Name</label>
              <input
                type="text"
                value={onlineName}
                onChange={(e) => setOnlineName(e.target.value)}
                placeholder="Online Store"
                className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-stone-800 text-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-stone-500 text-xs mb-1 block">Processor *</label>
              <Segmented ariaLabel="Processor" value={onlineProcessor}
                options={[{ id: 'dejavoo', label: 'Dejavoo' }, { id: 'magtek', label: 'MagTek' }]}
                onChange={(v) => { setOnlineProcessor(v as 'dejavoo' | 'magtek'); setOnlineError(null); }} />
            </div>
            {onlineProcessor === 'magtek' && (
              <div className="sm:col-span-2" data-testid="online-magtek">
                {!onlineLocationId ? (
                  <p className="text-stone-400 text-xs py-1">Select a location to check MagTek readiness.</p>
                ) : magtekReadiness === undefined ? (
                  <div className="flex items-center gap-2 text-stone-400 text-sm py-1"><RubyGemMark size={14} drawing /> Checking…</div>
                ) : magtekReadiness === null ? (
                  <p className="text-ruby-deep text-xs py-1">Could not check MagTek credentials. Try again.</p>
                ) : (
                  <div className="space-y-1.5">
                    <MagtekReadinessChips readiness={magtekReadiness} />
                    {!magtekReady(magtekReadiness) && (
                      <p className="text-xs text-ruby-deep">Credentials missing. <Link to={PAYMENT_PROCESSORS_PATH} className="font-semibold underline">Open Payment processors</Link></p>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className={`sm:col-span-2 ${onlineProcessor === 'magtek' ? 'hidden' : ''}`}>
              <label className="text-stone-500 text-xs mb-1 block">Payment processor (Dejavoo Cloud) *</label>
              {/* Existing vs new toggle */}
              <div className="inline-flex gap-1 bg-stone-100 border border-stone-200 rounded-lg p-0.5 mb-2">
                <button type="button" onClick={() => setOnlineMode('existing')} className={`text-xs font-semibold px-3 py-1.5 rounded-md ${onlineMode === 'existing' ? 'bg-emerald-600 text-white' : 'text-stone-500'}`}>Use existing</button>
                <button type="button" onClick={() => setOnlineMode('new')} className={`text-xs font-semibold px-3 py-1.5 rounded-md ${onlineMode === 'new' ? 'bg-emerald-600 text-white' : 'text-stone-500'}`}>Add new</button>
              </div>

              {onlineMode === 'existing' ? (
                devicesLoading ? (
                  <div className="flex items-center gap-2 text-stone-400 text-sm py-2"><RubyGemMark size={14} drawing /> Loading processors...</div>
                ) : dejavooDevices.length === 0 ? (
                  <p className="text-amber-600 text-xs py-2">No Dejavoo Cloud processor yet — switch to "Add new" to create one.</p>
                ) : (
                  <SearchableDropdown
                    options={dejavooDevices.map(d => ({
                      value: d.deviceId,
                      label: `${d.deviceName}${d.tpnMasked ? ` — ${d.tpnMasked}` : ''}`,
                      subtitle: `${d.originLocationName}${d.originLocationId !== onlineLocationId ? ' (other store)' : ''}`,
                    }))}
                    value={onlineDeviceId || null}
                    onChange={(v) => setOnlineDeviceId(v || '')}
                    placeholder="— Select processor —"
                    ariaLabel="Payment processor"
                    searchPlaceholder="Search processors..."
                  />
                )
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input value={newProcName} onChange={(e) => setNewProcName(e.target.value)} placeholder="Processor nickname" className="bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-stone-800 text-sm" />
                  <input value={newProcTpn} onChange={(e) => setNewProcTpn(e.target.value)} placeholder="Dejavoo TPN" className="bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-stone-800 text-sm" />
                  <textarea value={newProcAuthToken} onChange={(e) => setNewProcAuthToken(e.target.value)} rows={2} placeholder="Auth Token — paste from this TPN's iPOSpays Integration page (required)" className="sm:col-span-2 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-stone-800 text-xs font-mono break-all resize-none" />
                  <p className="sm:col-span-2 text-stone-400 text-[11px]">New processor's home location = the location selected above. The Auth Token is the storefront card-form key. Creates the processor + the terminal in one step.</p>
                </div>
              )}
            </div>
          </div>
          {onlineError && (
            <p className="text-red-600 text-xs">{onlineError}</p>
          )}
          <div className="flex justify-end gap-2">
            <button
              onClick={resetOnlineForm}
              className="px-3 py-2 rounded-lg text-xs font-medium text-stone-600 bg-stone-100 hover:bg-stone-200"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateOnlineTerminal}
              disabled={onlineSubmitting || !onlineLocationId || (onlineProcessor === 'magtek' ? !magtekReady(magtekReadiness) : onlineMode === 'new' ? (!newProcName.trim() || !newProcTpn.trim() || !newProcAuthToken.trim()) : !onlineDeviceId)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 min-h-[36px]"
            >
              {onlineSubmitting ? (
                <><RubyGemMark size={14} drawing color="currentColor" /> Creating...</>
              ) : (
                'Create Online Terminal'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Terminal List */}
      {loading ? (
        <RubyLoader variant="section" />
      ) : terminals.length === 0 ? (
        <div className="text-center py-12 text-stone-500">
          <Monitor size={32} className="mx-auto mb-2 opacity-50" />
          <p>No POS terminals provisioned yet</p>
          <p className="text-stone-400 text-xs mt-1">Click "Create Terminal" to get started</p>
        </div>
      ) : (
        <div className="space-y-5">
          {(() => {
            // Group terminals by location
            const groups = new Map<string, { name: string; terminals: Terminal[] }>();
            for (const t of terminals) {
              const key = t.locationId || 'unknown';
              if (!groups.has(key)) groups.set(key, { name: t.locationName || t.locationId || 'Unknown Location', terminals: [] });
              groups.get(key)!.terminals.push(t);
            }
            return [...groups.entries()].map(([locId, group]) => (
              <div key={locId}>
                <div className="flex items-center gap-3 mb-2 px-1">
                  <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">{group.name}</span>
                  <div className="flex-1 h-px bg-stone-300" />
                  <span className="text-[10px] text-stone-400">{group.terminals.length} terminal{group.terminals.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="space-y-2">
                  {group.terminals.map((terminal) => {
                    // Online terminals are created already-active (no PROVISION# row,
                    // no device to activate) — never show them as pending/expired.
                    const isOnlineSalesTerminal = terminal.terminalType === 'online_sales';
                    const isOnline = onlineTerminals.has(terminal.terminalId);
                    // From the till's live connection + register row, not the provision row.
                    const display = terminalDisplayStatus(terminal, isOnline);
                    return (
                      <div
                        key={terminal.terminalId}
                        onClick={() => navigate(`/tenant/${tenantId}/pos-terminal/${terminal.terminalId}`, { state: { terminal, tenantName } })}
                        className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-4 cursor-pointer hover:border-stone-300 hover:shadow-md transition-all"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-stone-100 flex items-center justify-center shrink-0">
                            <Monitor size={18} className="text-stone-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-semibold text-stone-800 truncate">{terminal.registerName}</p>
                              {display.registered && !isOnlineSalesTerminal && (
                                <span title={isOnline ? 'Online' : 'Offline'}>
                                  <Activity size={14} className={isOnline ? 'text-emerald-500' : 'text-red-400'} />
                                </span>
                              )}
                              {terminal.terminalType === 'online_sales' && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700 border border-emerald-200 shrink-0">
                                  <Globe size={9} /> Online Sales
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-stone-500">{typeLabel(terminal.terminalType)}</p>
                          </div>
                          <span title={display.hint} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border shrink-0 ${LIGHT_BADGE[display.tone]}`}>
                            {display.label}
                          </span>
                          {!display.registered && display.label === 'Pending' && (
                            <div className="flex flex-col items-end gap-0.5 shrink-0">
                              <div
                                className="flex items-center gap-2 px-3 py-1.5 bg-stone-50 border border-stone-200 rounded-lg"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!terminal.provisionCodeMasked) handleCopyCode(terminal.provisionCode);
                                }}
                              >
                                <span className={`font-mono text-sm font-bold ${terminal.codeExpired ? 'text-stone-400 line-through' : 'text-amber-600'}`}>{terminal.provisionCode}</span>
                                {!terminal.provisionCodeMasked && <Copy size={12} className="text-stone-400" />}
                              </div>
                              {terminal.codeExpired ? (
                                <span className="text-[10px] font-medium text-rose-700">Code expired</span>
                              ) : formatCodeExpiry(terminal.codeExpiresAt) ? (
                                <span className="text-[10px] text-stone-500 flex items-center gap-1">
                                  <Clock size={9} /> Expires {formatCodeExpiry(terminal.codeExpiresAt)}
                                </span>
                              ) : null}
                            </div>
                          )}
                          {terminal.status === 'activated' && (
                            <div className="text-right shrink-0 hidden sm:block">
                              <div className="text-stone-500 text-xs flex items-center gap-1">
                                <Wifi size={10} className="text-emerald-500" />
                                {formatDateTime(terminal.activatedAt)}
                              </div>
                            </div>
                          )}
                          {!isOnlineSalesTerminal && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setNewCodeFor(terminal); }}
                              title="Generate a new activation code for this till"
                              className="inline-flex items-center gap-1.5 px-3 rounded-lg text-xs font-medium text-stone-700 bg-white border border-stone-200 hover:bg-stone-50 min-h-[44px] shrink-0"
                            >
                              <KeyRound size={14} />
                              <span className="hidden sm:inline">New code</span>
                            </button>
                          )}
                          <ChevronRight size={16} className="text-stone-400 shrink-0" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ));
          })()}
        </div>
      )}

      <ConfirmModal
        open={!!newCodeFor}
        title="Generate new code?"
        tone="amber"
        confirmLabel="Generate new code"
        busy={newCodeBusy}
        onCancel={() => { if (!newCodeBusy) setNewCodeFor(null); }}
        onConfirm={handleGenerateNewCode}
        message={
          <>
            <p>
              <b>{newCodeFor?.registerName}</b> keeps its register, location and settings.
              The current code stops working and the device using it is signed out now.
            </p>
            <p className="mt-2">The new code works once and expires in 14 days.</p>
          </>
        }
      />
      <TerminalNewCodeModal result={newCodeResult} onClose={() => setNewCodeResult(null)} />
    </div>
  );
};

export default PosTerminalTab;
